import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import { fetchScheduleById, selectSchedulesById } from "./slice"
import { Link, useParams } from "react-router-dom"
import { Helmet } from "react-helmet-async"
import Layout from "../../ui/Layout"
import NavMain from "../../app/NavMain"
import ScrollablePage from "../../ui/ScrollablePage"
import Paper from "../../ui/Paper"
import DataPageHeader from "../../ui/DataPageHeader"
import PropTypes from "prop-types"
import styled from "styled-components"
import { Attribute } from "../../ui/Attribute"
import { getTimeAgo } from "../timing/time-ago"
import AstrositeName from "../astrosites/AstrositeName"
import SunInfo from "../astrosites/SunInfo"
import MapPin from "../geography/MapPin"
import MoonPhase from "./MoonPhase"
import ScheduledObservationsTable from "../scheduled-observations/ScheduledObservationsTable"
import {
  fetchScheduledObservations,
  selectScheduledObservationsByContext,
  selectScheduledObservationsByIds,
} from "../scheduled-observations/slice"
import ErrorMsg from "../../ui/ErrorMsg"
import LoadingSpinner, { Backdrop } from "../../ui/LoadingSpinner"
import NameIDHeader from "../../ui/NameIDHeader"

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 1rem 1rem;
`

const ScheduleMapLabel = ({ schedule = {} }) => (
  <div>
    <Link to={`/astrosites/${schedule.astrosite}`}>{schedule.astrosite}</Link>
    {schedule?.location?.latitude_degrees && (
      <div>Latitude: {schedule.location.latitude_degrees}</div>
    )}
    {schedule?.location?.longitude_degrees && (
      <div>Longitude: {schedule.location.longitude_degrees}</div>
    )}
    {schedule?.location?.height_metres && (
      <div>Height: {schedule.location.height_metres}</div>
    )}
  </div>
)

ScheduleMapLabel.propTypes = {
  schedule: PropTypes.object,
}

const Schedule = () => {
  const dispatch = useDispatch()
  const schedulesByID = useSelector(selectSchedulesById)
  const { id } = useParams()
  const schedule = schedulesByID[id]

  useEffect(() => {
    dispatch(fetchScheduleById(id))
  }, [])

  const scheduleLocation = schedule?.location && {
    id: schedule.id,
    content: <ScheduleMapLabel schedule={schedule} />,
    latitude: schedule.location.latitude_degrees,
    longitude: schedule.location.longitude_degrees,
  }

  return (
    <Layout>
      <NavMain />
      <Helmet>
        <title>Schedule {id} - Astrosite</title>
      </Helmet>
      {!schedule?.recording && schedule._loadStatus === "loading" && (
        <Backdrop>
          <LoadingSpinner />
        </Backdrop>
      )}
      <ScrollablePage>
        <DataPageHeader
          title={<NameIDHeader name={"Schedule"} id={id} />}
          subtitle={
            <>
              {schedule && schedule._loadStatus === "rejected" && (
                <div>{`There is a problem fetching the Schedule with ID ${id}`}</div>
              )}
            </>
          }
        />
        <Container>
          {scheduleLocation && (
            <Paper>
              <MapPin
                pins={[scheduleLocation]}
                width={"100%"}
                height={"50em"}
              />
            </Paper>
          )}
          <Paper>
            <ScheduleDetails schedule={schedule} />{" "}
          </Paper>
        </Container>
        <ScheduledObservations scheduleId={id} />
      </ScrollablePage>
    </Layout>
  )
}

const CreatedContainer = styled.span`
  display: flex;
`

const ScheduleDetails = ({ schedule = {} }) => (
  <div>
    {schedule?.created && (
      <CreatedContainer>
        <span>Created {getTimeAgo(schedule?.created)} for &nbsp;</span>
        <AstrositeName astrositeId={schedule.astrosite} />
      </CreatedContainer>
    )}
    <SunInfo {...schedule?.sun} />
    {schedule?.moon_phase && <MoonPhase moon_phase={schedule.moon_phase} />}

    <h3>Technical details</h3>
    <Attribute>
      <dt>Passes file ID</dt>
      <dd>{schedule?.passes_file_id}</dd>
    </Attribute>
    <Attribute>
      <dt>Priority file</dt>
      <dd>{schedule?.priority_file}</dd>
    </Attribute>
    <Attribute>
      <dt>Schedule file</dt>
      <dd>{schedule?.schedule_file}</dd>
    </Attribute>
    <Attribute>
      <dt>Spacetrack list</dt>
      <dd>{schedule?.spacetrack_list}</dd>
    </Attribute>
    <Attribute>
      <dt>Passes file</dt>
      <dd>{schedule?.passes_file}</dd>
    </Attribute>
    <Attribute>
      <dt>Task uri</dt>
      <dd>{schedule?.task_uri}</dd>
    </Attribute>
  </div>
)

ScheduleDetails.propTypes = {
  schedule: PropTypes.object,
}

// Renders a table of the scheduled observations that the relevant schedule has
const ScheduledObservations = ({ scheduleId }) => {
  const dispatch = useDispatch()
  const [filter, setFilter] = useState()
  // How we apply a filter update. Filter state is entirely in the URL query parameters
  const updateFilter = (f = {}) => {
    setFilter(f)
    dispatch(
      fetchScheduledObservations({
        filter: {
          ...f,
          schedule_id: scheduleId,
        },
        page: {
          offset: 0,
          limit: 50,
        },
        context: `schedule/${scheduleId}`,
      })
    )
  }

  // we get any matching collections for this Observation
  const scheduledObservationMatches = useSelector((state) =>
    selectScheduledObservationsByContext(state, `schedule/${scheduleId}`)
  )
  // We get the full objects from the ids the match
  const scheduledObservations = useSelector((state) =>
    selectScheduledObservationsByIds(
      state,
      scheduledObservationMatches?.ids || []
    )
  )

  useEffect(() => {
    dispatch(
      fetchScheduledObservations({
        filter: {
          schedule_id: scheduleId,
        },
        page: {
          offset: 0,
          limit: 1000,
        },
        context: `schedule/${scheduleId}`,
      })
    )
  }, [])
  return (
    <div>
      <span>Scheduled observations</span>
      {scheduledObservationMatches?._loadStatus === "loading" && (
        <div>...loading scheduled observations</div>
      )}
      {scheduledObservationMatches?._loadStatus === "rejected" && (
        <ErrorMsg
          msg={"There is a problem fetching the schedules"}
          showIcon={true}
        />
      )}
      {scheduledObservationMatches?._loadStatus !== "loading" &&
        scheduledObservationMatches?.page?.total_count && (
          <div>
            showing {scheduledObservations.length} of{" "}
            {scheduledObservationMatches?.page?.total_count}
          </div>
        )}
      <ScheduledObservationsTable
        scheduledObservations={scheduledObservations}
        filter={filter}
        setFilter={updateFilter}
        stickyHeader={false}
      />
      {scheduledObservations.length != 0 &&
        scheduledObservationMatches?.page?.total_count >
          scheduledObservations.length && (
          <span>
            showing {scheduledObservations.length} of{" "}
            {scheduledObservationMatches?.page?.total_count}{" "}
            <Link to={`/scheduled/observations?schedule_id=${scheduleId}`}>
              view all
            </Link>
          </span>
        )}
    </div>
  )
}

ScheduledObservations.propTypes = {
  scheduleId: PropTypes.string.isRequired,
}

export default Schedule
